import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../lib/api';
import { SEO } from '../components/SEO';
import { VerifiedBadge, VerificationDetails, type AdvertiserVerification } from '../components/VerifiedBadge';

interface Property {
  id: string;
  type: string;
  purpose: string;
  title: string;
  description: string | null;
  price: number | string;
  area: number;
  rooms: number;
  bathrooms: number;
  floors: number;
  ageYears: number;
  city: string;
  district: string | null;
  address: string | null;
  features: string[] | null;
  images: string[] | null;
  coverImage: string | null;
  videoUrl: string | null;
  featured: boolean;
  status: string;
  publishedAt: string | null;
  reviewedAt: string | null;
  adLicenseNumber: string | null;
  owner: ({ id: string; name: string; phone: string | null } & AdvertiserVerification) | null;
  broker: ({ id: string; name: string; phone: string | null } & AdvertiserVerification) | null;
}

export function PropertyDetailPage() {
  const { id } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['property', id],
    queryFn: () => api.get<ApiSuccess<Property>>(`/api/properties/${id}`),
    enabled: !!id,
  });

  if (isLoading) return <div className="text-center py-16 text-gray-500">⟳ جاري التحميل…</div>;
  if (error || !data?.data) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="text-6xl mb-3">😕</div>
        <h1 className="text-2xl font-black text-navy-dark mb-2">العقار غير موجود</h1>
        <Link to="/properties"><Button variant="primary">العودة للعقارات</Button></Link>
      </div>
    );
  }

  const p = data.data;
  const images = (p.images && p.images.length ? p.images : (p.coverImage ? [p.coverImage] : []));

  // 🔍 بناء كلمات مفتاحية ديناميكية لكل عقار
  const purposeText = p.purpose === 'SALE' ? 'للبيع' : p.purpose === 'RENT' ? 'للإيجار' : p.purpose === 'INVESTMENT' ? 'للاستثمار' : '';
  const typeAr = ({
    VILLA: 'فيلا', APARTMENT: 'شقة', LAND: 'أرض', COMMERCIAL: 'عقار تجاري',
    OFFICE: 'مكتب', WAREHOUSE: 'مستودع', FARM: 'مزرعة', RESTHOUSE: 'استراحة',
  } as Record<string, string>)[p.type] ?? p.type;
  const seoTitle = `${p.title} — ${typeAr} ${purposeText} في ${p.city} 🏠`;
  const seoDescription = (p.description ?? `${typeAr} ${purposeText} في ${p.city}${p.district ? ` - ${p.district}` : ''} · ${p.area} م² · ${p.rooms} غرف · ${p.bathrooms} حمام · ${Number(p.price).toLocaleString('en-US')} ر.س. إعلان عقاري مرخّص من الهيئة العامة للعقار (REGA) — شركة صورة العقارية.`).slice(0, 280);
  const seoKeywords = `${typeAr} ${purposeText}, ${typeAr} ${purposeText} في ${p.city}, ${p.city} عقار, ${p.district ? p.district + ', ' : ''}عقارات ${p.city}, ${typeAr} ${p.city}, شراء ${typeAr}, ${p.purpose === 'SALE' ? 'بيع' : 'إيجار'} ${typeAr}, إعلان عقاري مرخص, البورصة العقارية, REGA, شركة صورة العقارية, img.sa`;
  const canonicalUrl = `https://img.sa/properties/${p.id}`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={p.coverImage ?? undefined}
        canonical={canonicalUrl}
        type="product"
        jsonLd={{
          '@type': 'RealEstateListing',
          name: p.title,
          description: p.description ?? seoDescription,
          url: canonicalUrl,
          image: [p.coverImage, ...(p.images ?? [])].filter(Boolean),
          datePosted: p.publishedAt ?? undefined,
          offers: {
            '@type': 'Offer',
            price: Number(p.price),
            priceCurrency: 'SAR',
            availability: p.status === 'ACTIVE' ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock',
            url: canonicalUrl,
            seller: {
              '@type': 'Organization',
              name: 'شركة صورة العقارية',
              url: 'https://img.sa',
            },
          },
          address: {
            '@type': 'PostalAddress',
            addressLocality: p.city,
            addressRegion: p.district ?? undefined,
            addressCountry: 'SA',
            streetAddress: p.address ?? undefined,
          },
          floorSize: p.area ? { '@type': 'QuantitativeValue', value: p.area, unitCode: 'MTK' } : undefined,
          numberOfRooms: p.rooms || undefined,
          numberOfBathroomsTotal: p.bathrooms || undefined,
          numberOfFullBathrooms: p.bathrooms || undefined,
          ...(p.adLicenseNumber ? {
            identifier: { '@type': 'PropertyValue', name: 'REGA License', value: p.adLicenseNumber },
          } : {}),
          provider: {
            '@type': 'RealEstateAgent',
            name: 'شركة صورة العقارية',
            url: 'https://img.sa',
            telephone: '+966114993666',
            address: { '@type': 'PostalAddress', addressCountry: 'SA' },
          },
        }}
      />
      {/* Hero with images */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="aspect-video bg-gradient-to-br from-navy to-navy-dark rounded-2xl overflow-hidden">
          {images[0] ? (
            <img src={images[0]} alt={p.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white text-6xl">🏠</div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          {images.slice(1, 5).map((img, i) => (
            <div key={i} className="aspect-video bg-gray-100 rounded-xl overflow-hidden">
              <img src={img} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
          {images.length < 5 && Array.from({ length: 4 - Math.max(0, images.length - 1) }).map((_, i) => (
            <div key={`ph-${i}`} className="aspect-video bg-gray-100 rounded-xl flex items-center justify-center text-gray-300 text-4xl">🏘️</div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardBody>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <h1 className="text-2xl font-black text-navy-dark mb-1">{p.title}</h1>
                  <p className="text-gray-500">📍 {p.city}{p.district ? ` - ${p.district}` : ''}{p.address ? ` - ${p.address}` : ''}</p>
                </div>
                <div className="flex gap-1 flex-wrap">
                  {p.featured && <Badge color="gold">⭐ مميّز</Badge>}
                  <Badge color="navy">{p.purpose === 'SALE' ? 'للبيع' : p.purpose === 'RENT' ? 'للإيجار' : p.purpose}</Badge>
                  <Badge color="green">{p.type}</Badge>
                </div>
              </div>

              {p.description && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <h3 className="font-bold text-ink mb-2">الوصف</h3>
                  <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-line">{p.description}</p>
                </div>
              )}
            </CardBody>
          </Card>

          {/* Specs */}
          <Card>
            <CardBody>
              <h3 className="font-bold text-ink mb-4">المواصفات</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Spec label="المساحة"  value={`${p.area} م²`} />
                <Spec label="الغرف"    value={p.rooms} />
                <Spec label="الحمامات" value={p.bathrooms} />
                <Spec label="الأدوار"  value={p.floors || '—'} />
                <Spec label="عمر العقار" value={p.ageYears ? `${p.ageYears} سنة` : 'جديد'} />
              </div>
              {p.features && p.features.length > 0 && (
                <>
                  <h3 className="font-bold text-ink mt-6 mb-3">المزايا</h3>
                  <div className="flex flex-wrap gap-2">
                    {p.features.map((f, i) => (
                      <Badge key={i} color="navy">✓ {f}</Badge>
                    ))}
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          {/* ✓ شارة الاعتماد الرسمية (شفافية + ثقة) */}
          {p.adLicenseNumber && p.reviewedAt && (
            <Card className="border-2 border-green-300 bg-green-50/50">
              <CardBody className="!py-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-black text-sm shrink-0">✓</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-green-800 text-sm">معتمد رسمياً</div>
                    <div className="text-[10px] text-green-700">شركة صورة العقارية</div>
                  </div>
                </div>
                <div className="space-y-1 text-[11px] text-green-900">
                  <div className="flex justify-between">
                    <span className="text-green-700">رقم الترخيص:</span>
                    <code dir="ltr" className="font-bold">{p.adLicenseNumber}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">تاريخ المراجعة:</span>
                    <span className="font-bold">{new Date(p.reviewedAt).toLocaleDateString('ar-SA')}</span>
                  </div>
                </div>
                <a
                  href="https://eservicesredp.rega.gov.sa/e-services/inquiry-about-the-real-estate-advertisement-license/form"
                  target="_blank" rel="noopener noreferrer"
                  className="block mt-2 text-center text-[10px] text-green-700 hover:underline font-bold"
                >
                  🔗 تحقق من الترخيص في الهيئة العامة للعقار
                </a>
              </CardBody>
            </Card>
          )}

          {/* بطاقة السعر + الأكشنز الرئيسية */}
          <Card>
            <CardBody>
              <div className="text-xs text-gray-500 mb-1">السعر</div>
              <div className="text-3xl font-black text-navy-dark mb-3">
                {Number(p.price).toLocaleString('en-US')} <span className="text-sm text-gray-500">ر.س</span>
              </div>

              {/* تواصل مع المعلن مباشرة (لو في رقم) — وإلا الشركة */}
              {(() => {
                const contact = p.broker ?? p.owner;
                const contactPhone = contact?.phone;
                const contactName = contact?.name;
                if (contactPhone) {
                  const msg = encodeURIComponent(
                    `السلام عليكم، أشاهد إعلانكم على شركة صورة العقارية:\n\n${p.title}\n${typeof window !== 'undefined' ? window.location.href : ''}`,
                  );
                  // 📱 تنظيف الرقم لـ wa.me (يبدأ بـ 966 بدون 00 أو +)
                  const cleanPhone = contactPhone.replace(/[^\d]/g, '').replace(/^0/, '966');
                  return (
                    <>
                      <a href={`https://wa.me/${cleanPhone}?text=${msg}`} target="_blank" rel="noopener" className="block">
                        <Button variant="gold" size="lg" className="w-full">
                          📱 تواصل واتساب مع {contactName?.split(' ')[0] ?? 'المعلن'}
                        </Button>
                      </a>
                      <a href={`tel:${contactPhone}`} className="block mt-2">
                        <Button variant="outline" size="lg" className="w-full">📞 اتصال مباشر</Button>
                      </a>
                    </>
                  );
                }
                // Fallback لو ما في رقم مالك/مسوّق — رقم الشركة
                return (
                  <>
                    <a href={`https://wa.me/966114993666?text=${encodeURIComponent(`أستفسر عن: ${p.title}`)}`} target="_blank" rel="noopener" className="block">
                      <Button variant="gold" size="lg" className="w-full">📱 تواصل عبر واتساب</Button>
                    </a>
                    <a href="tel:0114993666" className="block mt-2">
                      <Button variant="outline" size="lg" className="w-full">📞 اتصل الآن</Button>
                    </a>
                  </>
                );
              })()}
            </CardBody>
          </Card>

          {/* 🛡️ تفاصيل موثوقية المعلن */}
          {(p.owner || p.broker) && (
            <VerificationDetails user={p.broker ?? p.owner} />
          )}

          {/* بطاقة المسوّق/المالك مع أزرار تواصل */}
          {(p.owner || p.broker) && (() => {
            const contact = p.broker ?? p.owner!;
            const cleanPhone = contact.phone?.replace(/[^\d]/g, '').replace(/^0/, '966');
            const waMsg = encodeURIComponent(`السلام عليكم، أشاهد إعلانكم على شركة صورة العقارية:\n${p.title}`);
            return (
              <Card>
                <CardBody>
                  <h3 className="font-bold text-ink mb-3">{p.broker ? '🤝 المسوّق المسؤول' : '👤 صاحب العقار'}</h3>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-navy to-navy-dark text-white flex items-center justify-center text-xl font-black shrink-0">
                      {contact.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-bold text-navy-dark truncate">{contact.name}</div>
                      {contact.phone && (
                        <div className="text-ink-muted text-xs" dir="ltr">{contact.phone}</div>
                      )}
                      {/* شارة الموثوقية تحت الاسم */}
                      <div className="mt-1">
                        <VerifiedBadge user={contact} size="xs" hideIfNone={false} />
                      </div>
                    </div>
                  </div>
                  {contact.phone && cleanPhone && (
                    <div className="grid grid-cols-2 gap-2">
                      <a href={`https://wa.me/${cleanPhone}?text=${waMsg}`} target="_blank" rel="noopener"
                         className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-green-500 hover:bg-green-600 text-white text-xs font-bold transition">
                        <span>📱</span>
                        <span>واتساب</span>
                      </a>
                      <a href={`tel:${contact.phone}`}
                         className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-navy-dark hover:bg-navy text-white text-xs font-bold transition">
                        <span>📞</span>
                        <span>اتصال</span>
                      </a>
                    </div>
                  )}
                </CardBody>
              </Card>
            );
          })()}

          {/* 🔗 بطاقة مشاركة العقار */}
          <Card>
            <CardBody>
              <h3 className="font-bold text-ink mb-2 text-sm">🔗 مشاركة العقار</h3>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${p.title}\n${typeof window !== 'undefined' ? window.location.href : ''}`)}`}
                  target="_blank" rel="noopener"
                  className="flex items-center justify-center gap-1 px-2 py-2 rounded-xl bg-green-50 hover:bg-green-100 text-green-700 text-xs font-bold transition"
                >
                  <span>💬</span><span>واتساب</span>
                </a>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(p.title)}&url=${encodeURIComponent(typeof window !== 'undefined' ? window.location.href : '')}`}
                  target="_blank" rel="noopener"
                  className="flex items-center justify-center gap-1 px-2 py-2 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 text-xs font-bold transition"
                >
                  <span>𝕏</span><span>تويتر</span>
                </a>
              </div>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('✓ تم نسخ الرابط');
                }}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 text-ink text-xs font-bold transition border border-gray-200"
              >
                <span>📋</span>
                <span>نسخ رابط العقار</span>
              </button>
            </CardBody>
          </Card>
        </aside>
      </div>
    </div>
  );
}

function Spec({ label, value }: { label: string; value: number | string }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="font-bold text-ink">{value}</div>
    </div>
  );
}

